<template>
  <div class="top-navbar">
    <div class="d-flex align-items-center">
      <button class="sidebar-toggle-btn d-md-none mr-2" @click="$root.$emit('toggle-sidebar')">
        <i class="material-icons">menu</i>
      </button>

      <!-- App Switcher -->
      <b-dropdown no-caret variant="link" class="navbar-icon-dropdown app-switcher mr-2">
        <template #button-content>
          <i class="material-icons navbar-icon">apps</i>
        </template>
        <b-dropdown-header>Switch to</b-dropdown-header>
        <b-dropdown-item :href="transitionUrl" target="_blank">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">open_in_new</i> TransitionConnect
        </b-dropdown-item>
        <b-dropdown-item :href="enrollUrl" target="_blank">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">open_in_new</i> TransitionEnroll
        </b-dropdown-item>
      </b-dropdown>

      <!-- Brand -->
      <router-link :to="{ name: 'dashboard' }" class="navbar-brand-link">
        <img
          :src="schoolLogo || defaultBrandImage"
          :alt="brandText"
          class="navbar-brand-logo"
        />
      </router-link>
    </div>

    <div class="d-flex align-items-center ml-auto">
      <!-- Notifications -->
      <b-dropdown right no-caret variant="link" class="navbar-icon-dropdown notification-dropdown mx-1">
        <template #button-content>
          <i class="material-icons navbar-icon">notifications_none</i>
          <span v-if="unreadCount" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </template>
        <div class="notification-panel">
          <div class="notification-header">
            <h6 class="mb-0">Notifications</h6>
            <a v-if="unreadCount" href="#" class="mark-read-link" @click.prevent="markAllRead">
              <i class="material-icons" style="font-size: 18px;">done_all</i>
            </a>
          </div>
          <div class="notification-list">
            <div v-if="!notifications.length" class="notification-empty">
              <i class="material-icons" style="font-size: 36px; color: #c4c8d0;">notifications_off</i>
              <p>No notifications</p>
            </div>
            <div
              v-for="n in notifications"
              :key="n.id"
              class="notification-item"
              :class="{ 'notification-unread': !n.is_read }"
              @click="handleNotificationClick(n)"
            >
              <div class="notification-dot" v-if="!n.is_read"></div>
              <div class="notification-body">
                <p class="notification-text" v-html="n.message || n.description || 'New notification'"></p>
                <small class="notification-time">{{ formatTimeAgo(n.created_at) }}</small>
              </div>
            </div>
          </div>
        </div>
      </b-dropdown>

      <!-- Settings -->
      <b-dropdown v-if="isSuperAdmin || isSchoolOwner" right no-caret variant="link" class="navbar-icon-dropdown mx-1">
        <template #button-content>
          <i class="material-icons navbar-icon">settings</i>
        </template>
        <b-dropdown-header>Settings</b-dropdown-header>
        <b-dropdown-item v-if="isSchoolOwner" :to="{ name: 'school-profile' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">domain</i> School Profile
        </b-dropdown-item>
        <b-dropdown-item v-if="isSuperAdmin" :to="{ name: 'manage-schools' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">account_balance</i> Schools
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'manage-users' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">people</i> Users
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'integrations' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">api</i> Integrations
        </b-dropdown-item>
      </b-dropdown>

      <!-- User -->
      <b-dropdown right no-caret variant="link" class="navbar-icon-dropdown ml-1">
        <template #button-content>
          <div class="user-avatar">
            <span>{{ userInitials }}</span>
          </div>
        </template>
        <b-dropdown-header>Manage</b-dropdown-header>
        <b-dropdown-item :to="{ name: 'my-account' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">person</i> My Account
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'security' }">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">security</i> Security
        </b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item @click="handleLogout">
          <i class="material-icons mr-2" style="font-size: 18px; vertical-align: middle;">lock</i> Logout
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { get } from 'lodash';
import { USER_ROLES } from '../../common/constants';
import { formatTimeAgo } from '../../common/utils';
import defaultBrandImage from '@/assets/images/logos/logo.png';

export default {
  name: 'Navbar',
  data() {
    return {
      ws: null,
      defaultBrandImage,
      transitionUrl: process.env.VUE_APP_TRANSITION_URL || 'https://app.jointransition.com',
      enrollUrl: process.env.VUE_APP_ENROLL_URL || 'https://dashboard.healthcareenroll.io',
    };
  },
  computed: {
    ...mapGetters('auth', { user: 'getLoggedInUser', authToken: 'getAuthToken' }),
    ...mapGetters('navbarConfig', ['getNavbarBrandLogo', 'getNavbarBrandText']),
    ...mapGetters('notification', {
      notifications: 'getNotifications',
      unreadCount: 'getUnreadCount',
    }),
    schoolLogo() {
      return this.getNavbarBrandLogo || get(this.user, 'school.small_logo_url') || get(this.user, 'school.logo_url') || null;
    },
    brandText() {
      return this.getNavbarBrandText || 'TransitionCRM';
    },
    isSuperAdmin() {
      return this.user && this.user.role === USER_ROLES.SUPER_ADMIN;
    },
    isSchoolOwner() {
      return this.user && this.user.role === USER_ROLES.SCHOOL_OWNER;
    },
    userInitials() {
      if (!this.user) return '';
      var f = (this.user.first_name || '').charAt(0).toUpperCase();
      var l = (this.user.last_name || '').charAt(0).toUpperCase();
      return f + l;
    },
  },
  methods: {
    ...mapActions('navbarConfig', ['updateNavbarBrandDetails']),
    ...mapActions('notification', ['getAllNotifications', 'updateNotification', 'markAllRead', 'handleWebSocketMessage']),
    formatTimeAgo,
    async handleLogout() {
      this.disconnectWebSocket();
      this.$store.commit('notification/clearNotifications');
      await this.$store.dispatch('auth/logout');
      // Hard reload to /login to kill all in-flight axios requests so they
      // don't resolve with 401 and trigger error toasts from unmounted views.
      window.location.replace('/login');
    },
    handleNotificationClick(notification) {
      if (!notification.is_read) {
        this.updateNotification({ id: notification.id, data: { is_read: true } });
      }
    },
    connectWebSocket() {
      if (!this.user || !this.authToken) return;

      var backendUrl = process.env.VUE_APP_BACKEND_BASE_URL || 'http://localhost:8002';
      var wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      var wsHost = backendUrl.replace(/^https?:\/\//, '');
      var wsUrl = wsProtocol + '://' + wsHost + '/ws/notifications/' + this.user.id + '/?token=' + this.authToken;

      try {
        this.ws = new WebSocket(wsUrl);

        var self = this;
        this.ws.onmessage = function (event) {
          try {
            var message = JSON.parse(event.data);
            self.handleWebSocketMessage(message);
          } catch (e) {
            // ignore parse errors
          }
        };

        this.ws.onclose = function () {
          self.ws = null;
        };
      } catch (e) {
        // WebSocket connection failed (e.g. running with runserver instead of daphne)
      }
    },
    disconnectWebSocket() {
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    },
  },
  created() {
    if (this.user && this.user.school) {
      this.updateNavbarBrandDetails(this.user.school);
    }
    // Fetch initial notifications
    this.getAllNotifications({ limit: 20, offset: 0 });
    // Connect WebSocket for real-time updates
    this.connectWebSocket();
  },
  beforeDestroy() {
    this.disconnectWebSocket();
  },
};
</script>

<style scoped>
.sidebar-toggle-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--navbar-icon-color, #868e96);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}
.sidebar-toggle-btn:hover {
  color: var(--dark-color);
  background-color: rgba(0, 0, 0, 0.05);
}
.sidebar-toggle-btn .material-icons {
  font-size: 24px;
}
.navbar-brand-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}
.navbar-brand-logo {
  height: 36px;
  max-width: 130px;
  object-fit: contain;
}
.navbar-brand-text {
  font-weight: 600;
  font-size: 1rem;
  color: var(--dark-color);
}
.navbar-icon {
  color: var(--navbar-icon-color, #868e96);
  font-size: 22px;
  cursor: pointer;
  transition: color 0.15s ease;
}
.navbar-icon:hover {
  color: var(--dark-color);
}
.navbar-icon-dropdown ::v-deep .btn {
  padding: 0.25rem 0.5rem;
  line-height: 1;
}
.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Notification badge */
.notification-dropdown ::v-deep .btn {
  position: relative;
}
.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: var(--primary-color);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 8px;
  padding: 0 4px;
}

/* Notification panel */
.notification-panel {
  width: 340px;
  max-height: 420px;
  display: flex;
  flex-direction: column;
}
.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
}
.mark-read-link {
  color: var(--primary-color);
  text-decoration: none;
  display: flex;
  align-items: center;
}
.notification-list {
  overflow-y: auto;
  max-height: 360px;
}
.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: #868e96;
}
.notification-empty p {
  margin: 8px 0 0;
  font-size: 13px;
}
.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f4f4f4;
}
.notification-item:hover {
  background: #f8f9fa;
}
.notification-unread {
  background: rgba(59, 134, 245, 0.04);
}
.notification-dot {
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background-color: var(--primary-color);
  margin-top: 6px;
  margin-right: 10px;
}
.notification-body {
  flex: 1;
  min-width: 0;
}
.notification-text {
  margin: 0;
  font-size: 13px;
  color: var(--dark-color);
  line-height: 1.4;
}
.notification-time {
  color: #868e96;
  font-size: 11px;
}
</style>
